import { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISSED_KEY = 'install-prompt-dismissed';

function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function isStandalone(): boolean {
  return (
    (window.navigator as { standalone?: boolean }).standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches
  );
}

/**
 * Install prompt (FR-PLT-04).
 *
 * Two paths, because iOS has no `beforeinstallprompt`: Chrome and friends get a real
 * install button; iOS gets instructions, since Add to Home Screen can only be done by
 * hand there.
 *
 * The iOS branch is not cosmetic — FR-MSG-07 makes push notifications depend on the PWA
 * being installed to the Home Screen (iOS 16.4+), so a user who never installs will
 * later find reminders silently not arriving.
 */
export function InstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISSED_KEY) === 'yes') return;

    if (isIos()) {
      setShowIosHint(true);
      return;
    }

    const handler = (event: Event) => {
      event.preventDefault();
      setDeferred(event as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  function dismiss() {
    localStorage.setItem(DISMISSED_KEY, 'yes');
    setDeferred(null);
    setShowIosHint(false);
  }

  async function install() {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
  }

  if (!deferred && !showIosHint) return null;

  return (
    <div className="install-prompt" role="status">
      {deferred ? (
        <>
          <span>Install Expenses for offline entry.</span>
          <button type="button" className="btn btn-primary" onClick={() => void install()}>
            Install
          </button>
        </>
      ) : (
        <span>
          To install: tap <strong>Share</strong>, then <strong>Add to Home Screen</strong>.
          Push reminders need this on iOS.
        </span>
      )}
      <button type="button" className="btn btn-quiet" onClick={dismiss} aria-label="Dismiss">
        ✕
      </button>
    </div>
  );
}
